import {
  Box,
  ClickAwayListener,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Tooltip,
} from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'

import { ADMINFEATURE_KEYS } from 'components/common/adminFeatures'
import AlertGroupModal from 'components/AlertGroups/AlertGroupsModal'
import AlertHistoryModal from 'components/Admin/Organizations/AlertHistoryModal'
import DataBridgeModal from 'components/Admin/Organizations/DataBridge/DataBridgeModal'
import DataConfigDefaultsModal from 'components/WellList/DataConfigDefaults/DataConfigDefaultsModal'
import { Icon as Iconify } from '@iconify/react'
import MenuIcon from '@mui/icons-material/Menu'
import OilPriceCard from './OilPriceCard'
import ReportGroupModal from 'components/ReportGroups/ReportGroupsModal'
import TableExportModal from 'components/Admin/TableExportModal/TableExportModal'
import WellSeekerIcon from 'assets/wellScreenIcons/wsx-icon.png'
import WellSeekerSettingsModal from 'components/Admin/Organizations/WellSeekerSettingsModal'
import { appColors } from 'utils'
import { checkFeature } from '../userPermissions'
import { useAppSettings } from 'components/contexts/AppSettingContext'
import useInnovaAuth from 'components/common/hooks/useInnovaAuth'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import { useNavigate } from 'react-router'
import useOilPrices from 'components/common/hooks/useOilPrices'

const arrayHasAny = (array, candidates) => {
  if (!Array.isArray(array) || !Array.isArray(candidates)) return false
  if (array.length === 0 || candidates.length === 0) return false

  const set = new Set(array) // O(n)
  for (let i = 0; i < candidates.length; i++) {
    if (set.has(candidates[i])) return true // short-circuit
  }
  return false
}

const NavMenu = ({ isAuthenticated }) => {
  const { theme, getTooltipBgColor, getActionBarColor, getTextColor, getAgGridTheme } = useInnovaTheme()
  const [showDrawer, toggleDrawer] = useState(false)
  const [oilAndRigs, setOilAndRigs] = useState({
    brentCrude: null,
    wti: null,
    naturalGas: null,
    rigCounts: null,
  })
  const { actionBarWidth: leftPos } = useAppSettings()
  const [subMenuState, setSubMenuState] = useState({
    groups: false,
    userAdmin: false,
    desktopAppUpdates: false,
    innovaAdmin: false,
    innovaBilling: false,
    innovaLicensing: false,
    dataAcq: false,
    adminTools: false,
    innovaAccounting: false,
  })

  useEffect(() => {
    const openMenus = Object.keys(subMenuState).filter((key) => subMenuState[key])

    const latestKey = openMenus[openMenus.length - 1]

    if (latestKey) {
      scrollSubmenuIntoView(latestKey)
    }
  }, [subMenuState])

  const submenuHeaderRefs = useRef({})

  const scrollSubmenuIntoView = (key) => {
    const el = submenuHeaderRefs.current[key]
    if (!el) return

    requestAnimationFrame(() => {
      el.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'smooth' })
    })
  }

  const {
    fetchOilPrices,
    loading: isLoadingOilAndRigs,
    getBrentCrudePrice,
    getWtiPrice,
    getNaturalGasPrice,
    getRigCount,
  } = useOilPrices()
  const oilFetchedRef = useRef(false)

  useEffect(() => {
    fetchOilPrices()
    oilFetchedRef.current = true
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isLoadingOilAndRigs || !oilFetchedRef.current) return

    setOilAndRigs({
      brentCrude: getBrentCrudePrice(),
      wti: getWtiPrice(),
      naturalGas: getNaturalGasPrice(),
      rigCounts: getRigCount(),
    })
  }, [isLoadingOilAndRigs]) // eslint-disable-line react-hooks/exhaustive-deps

  const [showTableExport, setShowTableExport] = useState(false)
  const [showWellSeekerSettingsModal, setShowWellSeekerSettingsModal] = useState(false)
  const [showDataConfigDefaultsModal, setShowDataConfigDefaultsModal] = useState(false)
  const [showAlertGroupModal, setShowAlertGroupModal] = useState(false)
  const [showReportGroupModal, setShowReportGroupModal] = useState(false)
  const [showDataBridgeModal, setShowDataBridgeModal] = useState(false)
  const [showAlertHistoryModal, setShowAlertHistoryModal] = useState(false)
  const { userRole } = useInnovaAuth()
  const navigate = useNavigate()
  const paperRef = useRef(null)

  if (!isAuthenticated) return null

  const navigateTo = (route) => {
    navigate(route)
    toggleDrawer(false)
  }

  const toggleSubMenuState = (key) => {
    setSubMenuState((prev) => {
      const nextOpen = !prev[key]
      if (nextOpen) scrollSubmenuIntoView(key)
      return { ...prev, [key]: nextOpen }
    })
  }

  const getSubMenuState = (key) => {
    return subMenuState[key]
  }

  const HOVER_COLOR = '#2aa7c3'

  const listStyle = {
    border: '1px solid',
    borderColor: '#34c9eb',
    backgroundColor: 'inherit',
    boxSizing: 'border-box',
    width: '100%',
  }

  const ListItem = ({
    buttonRef,
    visible = true,
    route,
    icon,
    text,
    onClick,
    isNested,
    color,
    height = 27,
    width = 27,
  }) => {
    if (!visible) return null

    const isReactNode = typeof icon !== 'string'
    const isImage = typeof icon === 'string' && (icon.includes('data:image') || icon.includes('/static/'))

    return (
      <ListItemButton
        ref={buttonRef}
        onClick={onClick ? onClick : () => navigateTo(route)}
        sx={{
          paddingLeft: isNested ? '32px' : '10px',
          gap: 1,

          ...(theme === 'dark' && {
            '&:hover': {
              backgroundColor: '#1f1f1f',
            },
          }),

          '&:hover': {
            '& .MuiListItemText-root': { color: HOVER_COLOR },
            '& svg': { color: HOVER_COLOR, fill: HOVER_COLOR },
            '& img': { filter: 'brightness(0.95) saturate(1.1)' },
          },
        }}>
        {isReactNode ? (
          icon
        ) : isImage ? (
          <img alt='img' src={icon} style={{ height, width }} />
        ) : (
          <Iconify icon={icon} style={{ fontSize: '24px', color: color ? color : undefined }} />
        )}

        <ListItemText
          primary={text}
          sx={{
            color: theme === 'dark' ? appColors.headerTextColor : '#151515',
            '& .MuiListItemText-primary': {
              fontSize: '16px',
              fontFamily: 'Roboto',
              fontWeight: 'bold',
            },
          }}
        />
      </ListItemButton>
    )
  }

  const InnovaAdminList = () => {
    if (!userRole.innovaAdmin) return null

    return (
      <React.Fragment>
        {userRole?.adminAttributes?.superAdmin ||
        arrayHasAny(userRole?.adminAttributes?.adminFeatures, [
          ADMINFEATURE_KEYS.invoicesKey,
          ADMINFEATURE_KEYS.quotesKey,
        ]) ? (
          <React.Fragment>
            <ListItem
              text={'Innova Billing'}
              icon={'ant-design:dollar-circle-filled'}
              color={appColors.itemTextColor}
              buttonRef={(node) => {
                submenuHeaderRefs.current.innovaBilling = node
              }}
              onClick={() => toggleSubMenuState('innovaBilling')}
            />
            <Collapse in={getSubMenuState('innovaBilling')} timeout='auto' unmountOnExit>
              <List sx={listStyle}>
                {userRole?.adminAttributes?.superAdmin ||
                userRole?.adminAttributes?.adminFeatures?.includes(ADMINFEATURE_KEYS.invoicesKey) ? (
                  <ListItem
                    text={'Invoices'}
                    icon={'fa-solid:file-invoice-dollar'}
                    route={'/innovaAdmin/innovaBilling/invoices'}
                    isNested={true}
                  />
                ) : null}
                {userRole?.adminAttributes?.superAdmin ||
                userRole?.adminAttributes?.adminFeatures?.includes(ADMINFEATURE_KEYS.quotesKey) ? (
                  <ListItem
                    text={'Quotes'}
                    icon={'fa-solid:file-invoice'}
                    route={'/innovaAdmin/innovaBilling/quotes'}
                    isNested={true}
                  />
                ) : null}
                <ListItem
                  text={'Companys'}
                  icon={'ic:baseline-oil-barrel'}
                  route={'/innovaAdmin/innovaBilling/companys'}
                  isNested={true}
                />
                <ListItem
                  text={'Contacts'}
                  icon={'bxs:contact'}
                  route={'/innovaAdmin/innovaBilling/contacts'}
                  isNested={true}
                />
                <ListItem
                  text={'Software Details'}
                  icon={'ic:baseline-computer'}
                  route={'/innovaAdmin/innovaBilling/softwareDetails'}
                  isNested={true}
                />
                {userRole?.adminAttributes?.superAdmin ||
                userRole?.adminAttributes?.adminFeatures?.includes(ADMINFEATURE_KEYS.invoicesKey) ? (
                  <ListItem
                    text={'Software Billing'}
                    icon={'medical-icon:billing'}
                    route={'/innovaAdmin/innovaBilling/softwareBilling'}
                    isNested={true}
                  />
                ) : null}
                <List sx={listStyle}>
                  {userRole?.adminAttributes?.superAdmin ||
                  userRole?.adminAttributes?.adminFeatures?.includes(ADMINFEATURE_KEYS.bankAccountsKey) ? (
                    <ListItem
                      text={'Bank Accounts'}
                      icon={'ci:settings-filled'}
                      route={'/admin/bankRec/accounts'}
                      isNested={true}
                    />
                  ) : null}
                </List>
              </List>
            </Collapse>
          </React.Fragment>
        ) : null}
        {userRole?.adminAttributes?.superAdmin ||
        userRole?.adminAttributes?.adminFeatures?.includes(ADMINFEATURE_KEYS.licensesKey) ? (
          <React.Fragment>
            <ListItem
              text={'Innova Licensing'}
              icon={'mdi:license'}
              color={appColors.itemTextColor}
              onClick={() => toggleSubMenuState('innovaLicensing')}
              buttonRef={(node) => {
                submenuHeaderRefs.current.innovaLicensing = node
              }}
            />
            <Collapse in={getSubMenuState('innovaLicensing')} timeout='auto' unmountOnExit>
              <List sx={listStyle}>
                <ListItem
                  text={'Software Licenses'}
                  icon={'carbon:license-draft'}
                  route={'/innovaAdmin/licensing/licenses'}
                  isNested={true}
                />
                <ListItem
                  text={'License Requests'}
                  icon={'carbon:license-global'}
                  route={'/innovaAdmin/licensing/licenseRequest'}
                  isNested={true}
                />
                <ListItem
                  text={'License Deactivations'}
                  icon={'tabler:license-off'}
                  route={'/innovaAdmin/licensing/licenseDeactivations'}
                  isNested={true}
                />
              </List>
            </Collapse>
          </React.Fragment>
        ) : null}
        {userRole?.adminAttributes?.superAdmin ||
        arrayHasAny(userRole?.adminAttributes?.adminFeatures, [
          ADMINFEATURE_KEYS.organizationsKey,
          ADMINFEATURE_KEYS.dbTasksKey,
          ADMINFEATURE_KEYS.dbBackupsKey,
          ADMINFEATURE_KEYS.infrastructureKey,
          ADMINFEATURE_KEYS.vendorsKey,
          ADMINFEATURE_KEYS.wellListKey,
          ADMINFEATURE_KEYS.apiConfigKey,
          ADMINFEATURE_KEYS.wellSeekerAdminKey,
        ]) ? (
          <React.Fragment>
            <ListItem
              text={'Innova Admin'}
              icon={'ic:baseline-verified-user'}
              color={appColors.itemTextColor}
              onClick={() => toggleSubMenuState('innovaAdmin')}
              buttonRef={(node) => {
                submenuHeaderRefs.current.innovaAdmin = node
              }}
            />
            <Collapse in={getSubMenuState('innovaAdmin')} timeout='auto' unmountOnExit>
              <List sx={listStyle}>
                {userRole?.adminAttributes?.superAdmin ||
                userRole?.adminAttributes?.adminFeatures?.includes(ADMINFEATURE_KEYS.organizationsKey) ? (
                  <ListItem
                    text={'Organizations'}
                    icon={'clarity:users-solid'}
                    route={'/admin/innovaAdmin/orgs'}
                    isNested={true}
                  />
                ) : null}
                {userRole?.adminAttributes?.superAdmin ||
                userRole?.adminAttributes?.adminFeatures?.includes(ADMINFEATURE_KEYS.dbTasksKey) ? (
                  <ListItem
                    text={'DB Task Status'}
                    icon={'fluent-mdl2:database-sync'}
                    route={'/innovaAdmin/databaseRestoreStatus'}
                    isNested={true}
                  />
                ) : null}
                {userRole?.adminAttributes?.superAdmin ||
                userRole?.adminAttributes?.adminFeatures?.includes(ADMINFEATURE_KEYS.dbBackupsKey) ? (
                  <ListItem
                    text={'DB Backups'}
                    icon={'carbon:data-backup'}
                    route={'/innovaAdmin/databaseBackupFiles'}
                    isNested={true}
                  />
                ) : null}
                {userRole?.adminAttributes?.superAdmin ||
                userRole?.adminAttributes?.adminFeatures?.includes(ADMINFEATURE_KEYS.infrastructureKey) ? (
                  <React.Fragment>
                    <ListItem
                      text={'Infrastructure Health'}
                      icon={'material-symbols:dashboard-customize-rounded'}
                      route={'/admin/innovaAdmin/dashboard'}
                      isNested={true}
                    />
                    <ListItem
                      text={'ICDS Servers'}
                      icon={'fa-solid:server'}
                      route={'/admin/innovaAdmin/icds'}
                      isNested={true}
                    />
                    <ListItem
                      text={'AC Dashboard Status'}
                      icon={'solar:target-linear'}
                      route={'/admin/innovaAdmin/acDashboard'}
                      isNested={true}
                    />
                  </React.Fragment>
                ) : null}
                {userRole?.adminAttributes?.superAdmin ? (
                  <ListItem
                    text={'Innova Admin Users'}
                    icon={'dashicons:admin-users'}
                    route={'/innovaAdmin/innovaAdminUsers'}
                    isNested={true}
                  />
                ) : null}
                {userRole?.adminAttributes?.superAdmin ||
                userRole?.adminAttributes?.adminFeatures?.includes(ADMINFEATURE_KEYS.infrastructureKey) ? (
                  <React.Fragment>
                    <ListItem
                      text={'Endpoint Usage'}
                      icon={'mdi:web'}
                      route={'/admin/innovaAdmin/endPointUsage'}
                      isNested={true}
                    />
                  </React.Fragment>
                ) : null}
                {userRole?.adminAttributes?.superAdmin ||
                userRole?.adminAttributes?.adminFeatures?.includes(ADMINFEATURE_KEYS.vendorsKey) ? (
                  <ListItem
                    text={'Innova Vendors'}
                    icon={'fa-solid:warehouse'}
                    route={'/admin/innovaAdmin/innovaVendors'}
                    isNested={true}
                  />
                ) : null}
                {userRole?.adminAttributes?.superAdmin ||
                userRole?.adminAttributes?.adminFeatures?.includes(ADMINFEATURE_KEYS.wellListKey) ? (
                  <ListItem
                    text={'Well List'}
                    icon={'game-icons:oil-pump'}
                    route={'/well-list-multi-db'}
                    isNested={true}
                  />
                ) : null}
                {userRole?.adminAttributes?.superAdmin ||
                userRole?.adminAttributes?.adminFeatures?.includes(ADMINFEATURE_KEYS.wellSeekerAdminKey) ? (
                  <ListItem
                    text={'Website File Uploads'}
                    icon={'ic:baseline-upload-file'}
                    route={'/admin/innovaAdmin/adminWebsiteUploads'}
                    isNested={true}
                  />
                ) : null}
                {userRole?.adminAttributes?.superAdmin ||
                userRole?.adminAttributes?.adminFeatures?.includes(ADMINFEATURE_KEYS.apiConfigKey) ? (
                  <ListItem
                    text={'Api Config Mgmt'}
                    icon={'lucide:server-cog'}
                    route={'/admin/innovaAdmin/apiConfig'}
                    isNested={true}
                  />
                ) : null}
              </List>
            </Collapse>
          </React.Fragment>
        ) : null}
      </React.Fragment>
    )
  }

  const InnovaWellSeekerAdminList = () => {
    if (!userRole.innovaAdmin) return null
    if (
      !userRole?.adminAttributes?.superAdmin &&
      !userRole?.adminAttributes?.adminFeatures?.includes(ADMINFEATURE_KEYS.wellSeekerAdminKey)
    )
      return null

    return (
      <React.Fragment>
        <ListItem
          text={'Well Seeker Updates'}
          icon={
            <Box
              sx={{
                backgroundColor: theme === 'dark' ? appColors.headerTextColor : 'transparent',
                borderRadius: '16px',
                padding: '2px',
              }}>
              {<img alt='img' src={WellSeekerIcon} style={{ height: 27, width: 27 }} />}
            </Box>
          }
          buttonRef={(node) => {
            submenuHeaderRefs.current.desktopAppUpdates = node
          }}
          onClick={() => toggleSubMenuState('desktopAppUpdates')}
        />
        <Collapse in={getSubMenuState('desktopAppUpdates')} timeout='auto' unmountOnExit>
          <List sx={listStyle}>
            <ListItem
              text={'Upload Files'}
              icon={'akar-icons:cloud-upload'}
              route={'/admin/wellSeekerUpdateFiles'}
              isNested={true}
            />
            <ListItem
              text={'Config'}
              icon={'clarity:update-line'}
              route={'/admin/wellSeekerUpdateConfig'}
              isNested={true}
            />
            <ListItem
              text={'Feature Usage'}
              icon={'gala:chart'}
              route={'/admin/wellSeekerFeatureUsage'}
              isNested={true}
            />
          </List>
        </Collapse>
      </React.Fragment>
    )
  }

  const AdminList = () => {
    return (
      <React.Fragment>
        <ListItem
          text={'Validation & Auditing'}
          icon={'hugeicons:validation-approval'}
          buttonRef={(node) => {
            submenuHeaderRefs.current.validation = node
          }}
          onClick={() => toggleSubMenuState('validation')}
        />
        <Collapse in={getSubMenuState('validation')} timeout='auto' unmountOnExit>
          <List sx={listStyle}>
            <ListItem
              text={'Data QC'}
              icon={'grommet-icons:validate'}
              route={'/admin/dataQc'}
              isNested={true}
              visible={checkFeature(17, userRole?.roleAttributes?.featureId)}
            />
            <ListItem
              text={'Object Validation'}
              icon={'material-symbols:data-object'}
              route={'/admin/objectQc'}
              isNested={true}
              visible={userRole?.roleAttributes?.permissions?.admin}
            />
            <ListItem
              text={'Invoice QC'}
              icon={'mdi:invoice-check-outline'}
              route={'/admin/invoiceQc'}
              isNested={true}
              visible={checkFeature(19, userRole?.roleAttributes?.featureId)}
            />
            <ListItem
              text={'Audit Data'}
              icon={'ant-design:audit-outlined'}
              route={'/admin/auditData'}
              isNested={true}
              visible={checkFeature(15, userRole?.roleAttributes?.featureId)}
            />
            <ListItem
              text={'Database Changes'}
              icon={'eos-icons:database-migration'}
              isNested={true}
              route={'/admin/databaseChanges'}
              visible={checkFeature(17, userRole?.roleAttributes?.featureId)}
            />
            <ListItem
              text={'Alert History'}
              icon={'fluent:channel-alert-48-regular'}
              onClick={() => {
                toggleDrawer(false)
                setShowAlertHistoryModal(true)
              }}
              isNested={true}
              visible={userRole?.roleAttributes?.permissions?.admin}
            />
          </List>
        </Collapse>
        <ListItem
          text={'Catalogs'}
          icon={'grommet-icons:catalog'}
          buttonRef={(node) => {
            submenuHeaderRefs.current.catalogs = node
          }}
          onClick={() => toggleSubMenuState('catalogs')}
        />
        <Collapse in={getSubMenuState('catalogs')} timeout='auto' unmountOnExit>
          <List sx={listStyle}>
            <ListItem
              text={'Component Catalog'}
              icon={'grommet-icons:catalog'}
              route={'/admin/compCatalog'}
              isNested={true}
              visible={checkFeature(24, userRole?.roleAttributes?.featureId)}
            />
            <ListItem
              text={'Centralizer Catalog'}
              icon={'fluent:pipeline-add-20-filled'}
              route={'/admin/centralizerCatalog'}
              isNested={true}
              visible={checkFeature(46, userRole?.roleAttributes?.featureId)}
            />
            <ListItem
              text={'Stop Collar Catalog'}
              icon={'game-icons:spiked-collar'}
              route={'/admin/stopCollarCatalog'}
              isNested={true}
              visible={checkFeature(45, userRole?.roleAttributes?.featureId)}
            />
            <ListItem
              text={'MWD Probe Catalog'}
              icon={'fluent:pipeline-arrow-curve-down-20-filled'}
              route={'/admin/mwdProbeCatalog'}
              isNested={true}
              visible={checkFeature(53, userRole?.roleAttributes?.featureId)}
            />
          </List>
        </Collapse>
        <ListItem
          text={'Database Admin'}
          icon={'eos-icons:data-mining'}
          buttonRef={(node) => {
            submenuHeaderRefs.current.adminTools = node
          }}
          onClick={() => toggleSubMenuState('adminTools')}
        />
        <Collapse in={getSubMenuState('adminTools')} timeout='auto' unmountOnExit>
          <List sx={listStyle}>
            <ListItem
              text={'Table Export'}
              icon={'dashicons:database-export'}
              onClick={() => {
                toggleDrawer(false)
                setShowTableExport(true)
              }}
              isNested={true}
              visible={checkFeature(16, userRole?.roleAttributes?.featureId)}
            />
            <ListItem
              text={'Bulk Edit'}
              icon={'mdi:database-edit-outline'}
              route={'/admin/bulkEdit'}
              isNested={true}
              visible={checkFeature(18, userRole?.roleAttributes?.featureId)}
            />
          </List>
        </Collapse>
        <ListItem
          text={'Global Data Settings'}
          icon={'streamline-ultimate:coding-apps-website-big-data-database-globality-velocity'}
          onClick={() => toggleSubMenuState('globalDataSettings')}
          buttonRef={(node) => {
            submenuHeaderRefs.current.globalDataSettings = node
          }}
        />
        <Collapse in={getSubMenuState('globalDataSettings')} timeout='auto' unmountOnExit>
          <List sx={listStyle}>
            <ListItem
              text={'CRS'}
              icon={'clarity:map-line'}
              route={'/admin/crs'}
              isNested={true}
              visible={checkFeature(25, userRole?.roleAttributes?.featureId)}
            />
            <ListItem
              text={'Rig Names'}
              icon={'game-icons:oil-rig'}
              route={'/admin/rigNames'}
              isNested={true}
              visible={checkFeature(21, userRole?.roleAttributes?.featureId)}
            />
            <ListItem
              text={'Shipping Addresses'}
              icon={'fa6-solid:address-card'}
              route={'/admin/shippingAddresses'}
              isNested={true}
              visible={checkFeature(29, userRole?.roleAttributes?.featureId)}
            />
            <ListItem
              text={'Personnel Names'}
              icon={'bi:person-circle'}
              route={'/admin/personnelNames'}
              isNested={true}
              visible={checkFeature(20, userRole?.roleAttributes?.featureId)}
            />
            <ListItem
              text={'Wall Plot Templates'}
              icon={'mdi:land-plots'}
              isNested={true}
              route={'/admin/wpcTemplates'}
              visible={checkFeature(50, userRole?.roleAttributes?.featureId)}
            />
            <ListItem
              text={'Error Models'}
              icon={'ant-design:warning-filled'}
              isNested={true}
              route={'/errorModels'}
              visible={checkFeature(33, userRole?.roleAttributes?.featureId)}
            />
          </List>
        </Collapse>
        <ListItem
          text={'Data Bridge'}
          icon={'mdi:email-edit-outline'}
          onClick={() => {
            toggleDrawer(false)
            setShowDataBridgeModal(true)
          }}
          visible={userRole?.roleAttributes?.permissions?.admin}
        />
        <ListItem
          text={'Integrations'}
          icon={'icon-park-outline:api'}
          route={'/admin/integrations'}
          visible={checkFeature(30, userRole?.roleAttributes?.featureId)}
        />
      </React.Fragment>
    )
  }

  const UserAdminList = () => {
    if (
      !checkFeature(31, userRole?.roleAttributes?.featureId) &&
      !checkFeature(22, userRole?.roleAttributes?.featureId) &&
      !userRole.innovaAdmin
    ) {
      return null
    }

    return (
      <React.Fragment>
        <React.Fragment>
          <ListItem
            text={'Well Seeker Admin'}
            icon={
              <Box
                sx={{
                  backgroundColor: theme === 'dark' ? appColors.headerTextColor : 'transparent',
                  borderRadius: '16px',
                  padding: '2px',
                }}>
                {<img alt='img' src={WellSeekerIcon} style={{ height: 24, width: 24 }} />}
              </Box>
            }
            buttonRef={(node) => {
              submenuHeaderRefs.current.wellSeekerAdmin = node
            }}
            onClick={() => toggleSubMenuState('wellSeekerAdmin')}
            visible={userRole.innovaAdmin ? true : checkFeature(31, userRole?.roleAttributes?.featureId)}
          />
          <Collapse in={getSubMenuState('wellSeekerAdmin')} timeout='auto' unmountOnExit>
            <List sx={listStyle}>
              <ListItem
                text={'Usage & Users'}
                icon={'ic:baseline-supervised-user-circle'}
                route={'/admin/wellSeekerAdmin'}
                isNested={true}
              />
              <ListItem
                text={'Magnetic Models'}
                icon={'fluent-emoji-flat:magnet'}
                route={'/admin/wellSeekerMagneticModelsUpload'}
                isNested={true}
              />
              <ListItem
                text={'Unit Sets'}
                icon={'formkit:unit'}
                route={'/admin/wellSeekerUnitSetsUpload'}
                isNested={true}
              />
              <ListItem
                text={'Report Templates'}
                icon={'streamline-sharp:graphic-template-website-ui'}
                route={'/admin/wellSeekerReportTemplatesUpload'}
                isNested={true}
              />
              <ListItem
                text={'Report Covers'}
                icon={'jam:instant-picture'}
                route={'/admin/wellSeekerReportCoversUpload'}
                isNested={true}
              />
              <ListItem
                text={'Licenses'}
                icon={'clarity:license-line'}
                route={'/admin/wellSeekerLicenseUploads'}
                isNested={true}
              />
              <ListItem
                text={'Well Seeker Settings'}
                icon={'fluent-mdl2:content-settings'}
                onClick={() => {
                  toggleDrawer(false)
                  setShowWellSeekerSettingsModal(true)
                }}
                isNested={true}
                visible={checkFeature(31, userRole?.roleAttributes?.featureId)}
              />
            </List>
          </Collapse>
        </React.Fragment>

        <ListItem
          text={'User Management'}
          icon={'ic:round-admin-panel-settings'}
          buttonRef={(node) => {
            submenuHeaderRefs.current.userAdmin = node
          }}
          onClick={() => toggleSubMenuState('userAdmin')}
          visible={userRole.innovaAdmin ? true : checkFeature(22, userRole?.roleAttributes?.featureId)}
        />
        <Collapse in={getSubMenuState('userAdmin')} timeout='auto' unmountOnExit>
          <List sx={listStyle}>
            <ListItem
              text={'Users'}
              icon={'clarity:users-solid'}
              route={'/admin/userAdmin/users'}
              isNested={true}
              visible={userRole.innovaAdmin ? true : checkFeature(22, userRole?.roleAttributes?.featureId)}
            />
            <ListItem
              text={'Roles'}
              icon={'oui:app-users-roles'}
              route={'/admin/userAdmin/roles'}
              isNested={true}
              visible={userRole.innovaAdmin ? true : checkFeature(22, userRole?.roleAttributes?.featureId)}
            />
            <ListItem
              text={'Units'}
              icon={'ph:scales-bold'}
              route={'/admin/userAdmin/units'}
              isNested={true}
              visible={userRole.innovaAdmin ? true : checkFeature(22, userRole?.roleAttributes?.featureId)}
            />
            <ListItem
              text={'Database Access'}
              icon={'fluent:database-person-20-filled'}
              route={'/admin/userAdmin/databaseAccess'}
              isNested={true}
              visible={userRole.innovaAdmin ? true : checkFeature(22, userRole?.roleAttributes?.featureId)}
            />
          </List>
        </Collapse>

        <ListItem
          text={'Groups'}
          icon={'material-symbols:group'}
          buttonRef={(node) => {
            submenuHeaderRefs.current.groups = node
          }}
          onClick={() => toggleSubMenuState('groups')}
          visible={userRole.innovaAdmin ? true : checkFeature(22, userRole?.roleAttributes?.featureId)}
        />
        <Collapse in={getSubMenuState('groups')} timeout='auto' unmountOnExit>
          <List sx={listStyle}>
            <ListItem
              text={'Alert Groups'}
              icon={'fa6-solid:users-gear'}
              onClick={() => {
                toggleDrawer(false)
                setShowAlertGroupModal(true)
              }}
              isNested={true}
              visible={userRole.innovaAdmin ? true : checkFeature(22, userRole?.roleAttributes?.featureId)}
            />
            <ListItem
              text={'Report Groups'}
              icon={'carbon:report'}
              onClick={() => {
                toggleDrawer(false)
                setShowReportGroupModal(true)
              }}
              isNested={true}
              visible={userRole.innovaAdmin ? true : checkFeature(22, userRole?.roleAttributes?.featureId)}
            />
          </List>
        </Collapse>
      </React.Fragment>
    )
  }

  const DataAcqList = () => {
    if (!checkFeature(8, userRole?.roleAttributes?.featureId)) return null

    return (
      <React.Fragment>
        <ListItem
          text={'Data Acquisition'}
          icon={'mdi:access-point'}
          onClick={() => toggleSubMenuState('dataAcq')}
          buttonRef={(node) => {
            submenuHeaderRefs.current.dataAcq = node
          }}
        />
        <Collapse in={getSubMenuState('dataAcq')} timeout='auto' unmountOnExit>
          <List sx={listStyle}>
            <ListItem
              text={'WITSML Servers'}
              icon={'ant-design:database-filled'}
              route={'/witsmlServers'}
              isNested={true}
            />
            <ListItem
              text={'VDL WITS Templates'}
              icon={'material-symbols:dataset-outline'}
              route={'/witsTemplates'}
              isNested={true}
            />
            <ListItem
              text={'File History'}
              icon={'ic:baseline-upload-file'}
              route={'/dataAckFileHistory'}
              isNested={true}
            />
            <ListItem
              text={'Data Config Defaults'}
              icon={'material-symbols:settings'}
              isNested={true}
              onClick={() => {
                toggleDrawer(false)
                setShowDataConfigDefaultsModal(true)
              }}
            />
            <ListItem
              text={'Well Data Connections'}
              icon={'ic:baseline-upload-file'}
              route={'/dataMigration'}
              isNested={true}
              visible={userRole.innovaAdmin}
            />
          </List>
        </Collapse>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <Tooltip
        title={'Main Menu'}
        placement='bottom'
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: getTooltipBgColor(),
              fontSize: '12px',
              fontFamily: 'Roboto',
            },
          },
        }}>
        <IconButton sx={{ fontSize: '36px' }} onClick={() => toggleDrawer(!showDrawer)} size='large'>
          <MenuIcon sx={{ fontSize: 'inherit' }} />
        </IconButton>
      </Tooltip>
      {showTableExport ? <TableExportModal onClose={() => setShowTableExport(false)} /> : null}
      {showDataConfigDefaultsModal ? (
        <DataConfigDefaultsModal
          open={showDataConfigDefaultsModal}
          onClose={() => setShowDataConfigDefaultsModal(false)}
        />
      ) : null}
      {showWellSeekerSettingsModal ? (
        <WellSeekerSettingsModal
          onClose={() => setShowWellSeekerSettingsModal(false)}
          organization={userRole?.organization}
        />
      ) : null}
      {showAlertGroupModal && (
        <AlertGroupModal organization={userRole?.organization} onCancel={() => setShowAlertGroupModal(false)} />
      )}
      {showReportGroupModal && (
        <ReportGroupModal organization={userRole?.organization} onCancel={() => setShowReportGroupModal(false)} />
      )}
      {showDataBridgeModal ? (
        <DataBridgeModal onClose={() => setShowDataBridgeModal(false)} organization={userRole?.organization} />
      ) : null}
      {showAlertHistoryModal ? (
        <AlertHistoryModal
          onClose={() => setShowAlertHistoryModal(false)}
          organization={userRole?.organization}
          showAlertHistory={true}
        />
      ) : null}
      <Drawer
        variant='persistent'
        anchor='left'
        open={showDrawer}
        onClose={() => toggleDrawer(false)}
        PaperProps={{ ref: paperRef }}
        sx={{
          '& .MuiDrawer-paper': {
            left: `${leftPos}px`,
            backgroundColor: getActionBarColor(),
            color: getTextColor(),
          },
        }}>
        <ClickAwayListener
          onClickAway={(e) => {
            if (!showDrawer) return
            if (e.screenX < paperRef.current?.getBoundingClientRect()?.right) return
            if (paperRef.current?.contains(e.target)) return
            toggleDrawer(false)
          }}
          mouseEvent='onMouseDown'
          touchEvent='onTouchStart'>
          <Box className={getAgGridTheme()} sx={{ color: appColors.headerTextColor, transition: 'all 0.3s ease' }}>
            <OilPriceCard
              color={theme === 'dark' ? appColors.headerTextColor : '#151515'}
              brentOilPrice={oilAndRigs?.brentCrude}
              wtiOilPrice={oilAndRigs?.wti}
              naturalGassPrice={oilAndRigs?.naturalGas}
              rigCounts={oilAndRigs?.rigCounts}
            />
            <List sx={{ width: '100%', px: 0.5 }}>
              <Divider variant='middle' />
              <InnovaAdminList />
              <InnovaWellSeekerAdminList />
              <AdminList />
              <DataAcqList />
              <UserAdminList />
            </List>
          </Box>
        </ClickAwayListener>
      </Drawer>
    </React.Fragment>
  )
}

export default NavMenu
